import * as React from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Globe, Mail, Server, LayoutGrid, ArrowRight,
  AtSign, HardDrive, Send, Layers
} from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AddPlanDomainDialog } from "@/components/account/AddPlanDomainDialog";
import { useEnrollment } from "@/hooks/useEnrollments";
import type { EnrollmentDto } from "@/types/api";

export function PlanDetailPage() {
  const { id } = useParams();
  const subId = Number(id);
  const { enrollment, isLoading, error } = useEnrollment(subId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !enrollment) {
    return (
      <div className="space-y-4">
        <BackLink />
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          We couldn't find that plan. It may have been cancelled.
        </CardContent></Card>
      </div>
    );
  }

  const e = enrollment;
  const Icon = planIcon(e.plan_type);
  const price = billing(e);
  const active = e.status === "active";

  return (
    <div className="space-y-5">
      <BackLink />

      {/* Plan header */}
      <Card className="elevate">
        <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-accentSoft text-primary">
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{e.plan_name}</h1>
                <Badge variant={active ? "success" : "outline"} className="capitalize">{e.status}</Badge>
              </div>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {price.label}<span>{price.cadence}</span>
                {e.renews_at && <> · renews {new Date(e.renews_at).toLocaleDateString()}</>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm font-medium">
            <Globe className="h-4 w-4 text-muted-foreground" />
            {e.domain_count} of {e.domain_capacity < 0 ? "∞" : e.domain_capacity} domain{e.domain_count === 1 ? "" : "s"}
          </div>
        </CardContent>
      </Card>

      {/* Pool usage */}
      <div>
        <h2 className="mb-2 font-display text-lg font-semibold">Plan pool</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <PoolStat icon={AtSign} label="Mailboxes" used={e.pool.mailboxes_used} total={e.pool.mailboxes_pool} />
          <PoolStat icon={HardDrive} label="Storage" unit=" GB" used={e.pool.storage_gb_used} total={e.pool.storage_gb_pool} />
          <PoolStat icon={Layers} label="Websites" used={e.pool.websites_used} total={e.pool.websites_pool} />
          <PoolStat icon={Send} label="Bulk emails" used={e.pool.bulk_emails_used} total={e.pool.bulk_emails_pool} />
        </div>
      </div>

      {/* Domains */}
      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <h2 className="font-display text-lg font-semibold">Domains</h2>
          {active && (e.domain_capacity < 0 || e.domain_count < e.domain_capacity) && (
            <AddPlanDomainDialog
              subscriptionId={e.subscription_id}
              planName={e.plan_name}
              slotsLeft={e.domain_capacity < 0 ? -1 : e.domain_capacity - e.domain_count}
            />
          )}
        </div>
        {e.domains.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
            No domains attached to this plan yet.
          </CardContent></Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {e.domains.map(d => (
              <Link
                key={d.id}
                to={`/domains/${d.id}`}
                className="group flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-brand-sm"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-accentSoft text-primary">
                  <Globe className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{d.domain_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {d.mailboxes_used} mailbox{d.mailboxes_used === 1 ? "" : "es"} · {d.services_count} service{d.services_count === 1 ? "" : "s"}
                  </p>
                </div>
                <StatusDot status={d.status} />
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
      <ArrowLeft className="h-4 w-4" /> All plans
    </Link>
  );
}

function planIcon(type: string) {
  const t = (type || "").toLowerCase();
  if (t.includes("hosting")) return Server;
  if (t.includes("portfolio")) return LayoutGrid;
  return Mail;
}

function billing(e: EnrollmentDto) {
  const price = e.billing_months === 12 ? e.price_annually
    : e.billing_months === 3 ? e.price_quarterly : e.price_monthly;
  const cadence = e.billing_months === 12 ? "/yr" : e.billing_months === 3 ? "/qtr" : "/mo";
  const sym = e.currency === "USD" ? "$" : `${e.currency} `;
  return { label: `${sym}${price.toFixed(2)}`, cadence };
}

function PoolStat({
  icon: Icon, label, used, total, unit = ""
}: { icon: React.ComponentType<{ className?: string }>; label: string; used: number; total: number; unit?: string }) {
  const unlimited = total < 0;
  const pct = unlimited || total === 0 ? 0 : Math.min(100, Math.round((used / total) * 100));
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <p className="mt-1 text-lg font-semibold text-foreground">
          {round(used)}{unit}{" "}
          <span className="text-sm font-normal text-muted-foreground">of {unlimited ? "∞" : `${round(total)}${unit}`}</span>
        </p>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
        </div>
      </CardContent>
    </Card>
  );
}

function StatusDot({ status }: { status: string }) {
  const s = (status || "").toLowerCase();
  const color = s === "active" ? "bg-brand-green" : s.includes("pending") || s.includes("transfer") ? "bg-amber-500" : "bg-muted-foreground";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs capitalize text-muted-foreground">
      <span className={`h-1.5 w-1.5 rounded-full ${color}`} />
      {s || "—"}
    </span>
  );
}

function round(n: number) {
  return Math.round((n + Number.EPSILON) * 10) / 10;
}
